<template>
  <div>
    <aw-full-tiles
      v-if="!verticalOnly"
      :content="displayTiles"
      background-color="colorBackground2"
      :analytics-banner-interaction="analyticsBannerInteraction"
      :analytics-measurable="analyticsMeasurable"
    />
    <aw-vertical-tiles
      v-else
      :content="displayTiles"
      background-color="colorBackground2"
      :is-mobile-carousel="false"
      :analytics-banner-interaction="analyticsBannerInteraction"
      :analytics-measurable="analyticsMeasurable"
    />
    <aw-spinner v-if="isLoading" color="currentColor" :class="$style.loadMoreSpinner" />
    <div v-else-if="displayTiles.components.length" :class="$style.loadMore">
      <lv-button v-if="displayLoadMore" styling="secondary-dark" @click="loadMorePage">
        {{ $awt('aw.pages.aggregated_common.load_more') }}
        <lv-icon name="arrow-down-16" :size="16" />
      </lv-button>
    </div>
    <aw-empty v-else>
      {{ $awt('aw.pages.aggregated_common.no_results') }}
    </aw-empty>
  </div>
</template>

<script>
  import AwVerticalTiles from '~~/common/components/AwStandardPartials/AwVerticalTiles.vue';
  import { LvIcon, LvButton } from '~~/common/components/loginet-vue-shop/index.mjs';
  import otqs from '~~/common/utils/objectToQueryString';
  import { defineAsyncComponent } from 'vue';
  import { mapState } from 'pinia';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import analyticsBannerInteractionMixin from '~~/common/mixins/analyticsBannerInteractionMixin.js';
  import { getBackgroundImages } from '~~/common/utils/imageHelper.js';

  export default {
    name: 'AwAggregatedPagesCards',
    components: {
      AwVerticalTiles,
      AwEmpty: defineAsyncComponent(() => import('~~/common/components/Common/AwEmpty')),
      AwSpinner: defineAsyncComponent(() => import('~~/common/components/Common/AwSpinner')),
      AwFullTiles: defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwFullTiles')),
      LvIcon,
      LvButton,
    },
    mixins: [analyticsBannerInteractionMixin],
    props: {
      pageType: {
        type: String,
        required: true,
        validator: p => ['press_release', 'news', 'csr', 'inspiration', 'career_news'].includes(p),
      },
      params: {
        type: Object,
        required: true,
      },
      hideLoadMoreButton: {
        type: Boolean,
        required: false,
      },
      verticalOnly: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        isLoading: false,
        data: [],
        paginationParams: {
          page: 1,
          limit: 12,
        },
        paginationData: {},
      };
    },
    computed: {
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      displayTiles () {
        const data = this.data.map((tile, index) => {
          const images = [
            {
              ...tile.media?.desktopCardImage,
              media: tile.media?.desktopCardImage && tile.media?.mobileCardImage ? `(min-width: ${this.$styleVariables.breakpointTablet})` : undefined,
            },
            {
              ...tile.media?.mobileCardImage,
              media: tile.media?.desktopCardImage && tile.media?.mobileCardImage ? `(max-width: ${this.$styleVariables.breakpointTablet})` : undefined,
            },
          ];

          const contentImage = getBackgroundImages(this.screenRange['tablet-min'] ? images : images.reverse())[0];

          return {
            id: index,
            type: 'standard',
            layoutTemplate: 'tiles',
            title: tile.title,
            contentText: tile?.lead,
            dateTag: tile.publishedAt,
            showPlaceholder: true,
            media: {
              contentImage,
            },
            // mobileCardImage
            button: {
              alignment: 'center',
              items: [
                {
                  text: this.$awt('aw.pages.aggregated_common.tile.button_title'),
                  link: tile?.url,
                  type: 'small-secondary-dark',
                  mobileType: 'small-secondary-dark',
                },
              ],
            },
            contentBackgroundColorOnly: false,
            horizontalLayout: false,
            tags: ['csr', 'inspiration'].includes(this.pageType) ? tile.tags : [],
          };
        });
        return {
          id: 0,
          layoutType: 'full_tiles',
          spacingType: 'positive',
          fullWidth: false,
          measurablePromotion: false,
          components: data,
        };
      },
      displayLoadMore () {
        return this.paginationData.pageCount > this.paginationParams.page && !this.hideLoadMoreButton;
      },
    },
    mounted () {
      this.fetchData();
    },
    methods: {
      loadMorePage () {
        this.paginationParams.page += 1;
        this.fetchData();
      },
      async fetchData () {
        this.page = 1;
        this.isLoading = true;
        const params = {
          highlighted: false,
          ...this.paginationParams,
          ...this.params,
        };
        const result = await this.$api.get(`cms/tag-groups/${this.pageType}${otqs(params)}`);
        this.data = this.data.concat(result.data?.contentVersions);
        this.paginationData = {
          itemCount: result.data?.itemCount,
          pageCount: result.data?.pageCount,
        };
        this.isLoading = false;
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.loadMore {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;

  &Spinner {
    margin-top: 20px;
    color: $color-text-primary;
    @include tablet(min) {
      margin-top: 40px;
    }
  }

  @include tablet(min) {
    margin-top: 40px;
  }
}
</style>
